// libraries
import React, { useMemo } from 'react';

import { Card, Col, Row, Tag, Empty } from 'antd';
import { ColumnType } from 'antd/lib/table';

// components
import { StyledTable } from '../entity/shared/components/styled/StyledTable';
import { useTranslation } from '../../i18n/useTranslation';
import { colorUtilService, ColumnChart } from '../shared/charts';
import { WidgetCardHeader } from './WidgetCardHeader';
import { ChartWidgetCard } from './ChartWidgetCard';
import { EntityNameLink } from './EntityNameLink';
import { RecommendationModule } from '../recommendations/RecommendationModule';
import { useEntityRegistry } from '../useEntityRegistry';
import { PrivilegeGate } from '../shared/PrivilegeGate';

// graphql
import { useListRecommendationsQuery } from '../../graphql/recommendations.generated';
import { useAggregateAcrossEntitiesQuery } from '../../graphql/search.generated';
import {
    EntityType,
    GovernanceType,
    RecommendationModule as RecommendationModuleType,
    ScenarioType,
} from '../../types.generated';
import { useUserContext } from '../context/useUserContext';
import {
    COMMON_ASSET_ENTITY_TYPES,
    getAggDisplayName,
    getFacetAggregations,
    MOST_POPULAR_MODULE_ID,
    RECENTLY_EDITED_MODULE_ID,
    RECENTLY_VIEWED_MODULE_ID,
    TOP_TAGS_MODULE_ID,
    TOP_TERMS_MODULE_ID,
} from './utils';
import { DOMAINS_FILTER_NAME, OWNERS_FILTER_NAME } from '../search/utils/constants';
import { getFieldValues, mergeDashboardFilters, useDashboardFilters } from './dashboardFilters';

// images
import { ReactComponent as DataProductIcon } from '../../images/icon-data-product.svg';
import { ReactComponent as DataDomainIcon } from '../../images/icon-data-domain.svg';
import { ReactComponent as DatasetIcon } from '../../images/icon-dataset.svg';

/**
 * Shared empty state for the dashboard tables. `image={null}` drops antd's default
 * illustration (which the PDF export renders as a missing image), and `imageStyle` collapses
 * the height antd still reserves for the image container so only the text shows.
 */
const getNoDataLocale = (description: string) => ({
    emptyText: <Empty image={null} imageStyle={{ display: 'none' }} description={description} />,
});

interface AssetRow {
    id: number;
    urn: string;
    asset_name: string;
    asset_type: EntityType;
    domain: string;
    // Datasets expose `exists`; false means the asset was deleted and must not be clickable.
    exists?: boolean;
}

interface RecentlyAccessedAssetRow extends AssetRow {
    timestamp: string;
}

interface GlossaryTermRow {
    id: number;
    urn: string;
    term_name: string;
    domain: string;
    governance_status: string;
    governance_display_name: string;
    // Datasets expose `exists`; false means the asset was deleted and must not be clickable.
    exists?: boolean;
}

const getStatusClass = (status) => {
    return {
        [GovernanceType.UnderReview]: 'base-tag',
        [GovernanceType.Certified]: 'ant-tag-success',
    }[status];
};

const getGlossaryTermColumns = (entityRegistry): ColumnType<GlossaryTermRow>[] => [
    {
        width: '40%',
        title: 'Term Name',
        dataIndex: 'term_name',
        key: 'term_name',
        render: (value: string, record: GlossaryTermRow) => (
            <EntityNameLink
                name={value}
                to={entityRegistry.getEntityUrl(EntityType.GlossaryTerm, record.urn)}
                exists={record.exists}
            />
        ),
    },
    {
        width: '30%',
        title: 'Domain',
        dataIndex: 'domain',
        key: 'domain',
        render: (value: string) => (
            <>
                {value ? (
                    <Tag className="label-tag ma-0">
                        <DataDomainIcon />
                        <span>{value}</span>
                    </Tag>
                ) : (
                    <div>&ndash;</div>
                )}
            </>
        ),
    },
    {
        width: '30%',
        title: 'Governance Status',
        dataIndex: 'governance_display_name',
        key: 'governance_status',
        render: (value: string, record: GlossaryTermRow) =>
            value && record.governance_status !== GovernanceType.None ? (
                <Tag className={`${getStatusClass(record.governance_status)} label-tag f-tag-borderless  ma-0`}>
                    {value}
                </Tag>
            ) : (
                <div>&ndash;</div>
            ),
    },
];

const getMostAccessedAssetsGrid = (entityRegistry): ColumnType<AssetRow>[] => [
    {
        width: '33.33%',
        title: 'Asset Name',
        dataIndex: 'asset_name',
        key: 'asset_name',
        render: (value: string, record: AssetRow) => (
            <EntityNameLink
                name={value}
                to={entityRegistry.getEntityUrl(record.asset_type, record.urn)}
                exists={record.exists}
            />
        ),
    },
    {
        width: '33.33%',
        title: 'Asset Type',
        dataIndex: 'asset_type',
        key: 'asset_type',
        render: (value: string) => (
            <>
                {value ? (
                    <Row align="middle" gutter={4} wrap={false}>
                        <Col flex="none">
                            <Tag className="label-tag base-tag ma-0">
                                {value === 'Dataset' ? <DatasetIcon /> : <DataProductIcon />}
                            </Tag>
                        </Col>
                        <Col flex="none">
                            <div>{value}</div>
                        </Col>
                    </Row>
                ) : (
                    <div>&ndash;</div>
                )}
            </>
        ),
    },
    {
        width: '33.33%',
        title: 'Domain',
        dataIndex: 'domain',
        key: 'domain',
        render: (value: string) => (
            <>
                {value ? (
                    <Tag className="label-tag ma-0">
                        <DataDomainIcon />
                        <span>{value}</span>
                    </Tag>
                ) : (
                    <div>&ndash;</div>
                )}
            </>
        ),
    },
];

const getAssetsColumns = (entityRegistry): ColumnType<RecentlyAccessedAssetRow>[] => [
    {
        width: '25%',
        title: 'Asset Name',
        dataIndex: 'asset_name',
        key: 'asset_name',
        render: (value: string, record: RecentlyAccessedAssetRow) => (
            <EntityNameLink
                name={value}
                to={entityRegistry.getEntityUrl(record.asset_type, record.urn)}
                exists={record.exists}
            />
        ),
    },
    {
        width: '25%',
        title: 'Asset Type',
        dataIndex: 'asset_type',
        key: 'asset_type',
        render: (value: string) => (
            <>
                {value ? (
                    <Row align="middle" gutter={4} wrap={false}>
                        <Col flex="none">
                            <Tag className="label-tag base-tag ma-0">
                                {value === 'Dataset' ? <DatasetIcon /> : <DataProductIcon />}
                            </Tag>
                        </Col>
                        <Col flex="none">
                            <div>{value}</div>
                        </Col>
                    </Row>
                ) : (
                    <div>&ndash;</div>
                )}
            </>
        ),
    },
    {
        width: '25%',
        title: 'Domain',
        dataIndex: 'domain',
        key: 'domain',
        render: (value: string) => (
            <>
                {value ? (
                    <Tag className="label-tag ma-0">
                        <DataDomainIcon />
                        <span>{value}</span>
                    </Tag>
                ) : (
                    <div>&ndash;</div>
                )}
            </>
        ),
    },
    {
        width: '25%',
        title: 'Timestamp',
        dataIndex: 'timestamp',
        key: 'timestamp',
        render: (value: string) => <div>{value ? new Date(value).toLocaleString() : '&ndash;'}</div>,
    },
];

export const HomePageRecommendationV2 = () => {
    const { t } = useTranslation();
    const entityRegistry = useEntityRegistry();
    const { domainsColorMap } = colorUtilService;
    const { user } = useUserContext() ?? {};
    const userUrn = user?.urn || '';

    const dashboardFilters = useDashboardFilters();

    // Entities-per-domain series
    const { data: domainEntitiesData, loading: domainEntitiesLoading } = useAggregateAcrossEntitiesQuery({
        variables: {
            input: {
                query: '*',
                types: COMMON_ASSET_ENTITY_TYPES,
                facets: [DOMAINS_FILTER_NAME],
                searchFlags: { maxAggValues: 100 },
                orFilters: mergeDashboardFilters(dashboardFilters),
            },
        },
        fetchPolicy: 'no-cache',
    });

    // DataProducts-per-domain series
    const { data: domainDataProductsData, loading: domainDataProductsLoading } = useAggregateAcrossEntitiesQuery({
        variables: {
            input: {
                query: '*',
                types: [EntityType.DataProduct],
                facets: [DOMAINS_FILTER_NAME],
                searchFlags: { maxAggValues: 100 },
                orFilters: mergeDashboardFilters(dashboardFilters),
            },
        },
        fetchPolicy: 'no-cache',
    });

    const domainsLoading = domainEntitiesLoading || domainDataProductsLoading;

    const domainEntitiesFacets = domainEntitiesData?.aggregateAcrossEntities?.facets;
    const domainDataProductsFacets = domainDataProductsData?.aggregateAcrossEntities?.facets;

    const topDomains = useMemo(() => {
        const findAggs = (facets: typeof domainEntitiesFacets) => getFacetAggregations(facets, DOMAINS_FILTER_NAME);

        const byUrn = new Map<string, { name: string; entities: number; dataProducts: number }>();

        const upsert = (urn: string, name: string) => {
            const existing = byUrn.get(urn);
            if (existing) {
                // Prefer the first non-fallback name that comes in across both API calls.
                if (existing.name === urn.split(':').pop() && name !== urn.split(':').pop()) {
                    existing.name = name;
                }
                return existing;
            }
            const created = { name, entities: 0, dataProducts: 0 };
            byUrn.set(urn, created);
            return created;
        };

        findAggs(domainEntitiesFacets).forEach((agg) => {
            upsert(agg.value, getAggDisplayName(agg)).entities = agg.count;
        });
        findAggs(domainDataProductsFacets).forEach((agg) => {
            upsert(agg.value, getAggDisplayName(agg)).dataProducts = agg.count;
        });

        return Array.from(byUrn.values())
            .map((d) => ({ ...d, combinedTotal: d.entities + d.dataProducts }))
            .filter((d) => d.combinedTotal > 0)
            .sort((a, b) => b.combinedTotal - a.combinedTotal)
            .slice(0, 10);
    }, [domainEntitiesFacets, domainDataProductsFacets]);

    const domainsChartCategories = topDomains.map((d) => d.name);

    const domainsChartSeries = [
        {
            name: t('Entities'),
            color: domainsColorMap.entities,
            data: topDomains.map((d) => d.entities),
        },
        {
            name: t('Data Products'),
            color: domainsColorMap.dataProducts,
            data: topDomains.map((d) => d.dataProducts),
        },
    ];

    /**
     * listRecommendations takes the dashboard filters as direct input fields (not orFilters);
     * omit each field when nothing is selected. dataProducts is out of the current sprint scope.
     */
    const recommendationOwners = getFieldValues(dashboardFilters, OWNERS_FILTER_NAME);
    const recommendationDomains = getFieldValues(dashboardFilters, DOMAINS_FILTER_NAME);

    // Fetch recommendations — skip until userUrn is available
    const { data, loading: recommendationsLoading } = useListRecommendationsQuery({
        variables: {
            input: {
                userUrn,
                requestContext: {
                    scenario: ScenarioType.Home,
                },
                limit: 10,
                ...(recommendationOwners.length > 0 && { owners: recommendationOwners }),
                ...(recommendationDomains.length > 0 && { domains: recommendationDomains }),
            },
        },
        skip: !userUrn,
        fetchPolicy: 'no-cache',
    });

    const recommendationModules = data?.listRecommendations?.modules;

    const mostAccessedModule = recommendationModules?.find((m) => m.moduleId === MOST_POPULAR_MODULE_ID);
    const recentlyViewedModule = recommendationModules?.find((m) => m.moduleId === RECENTLY_VIEWED_MODULE_ID);
    const recentlyEditedModule = recommendationModules?.find((m) => m.moduleId === RECENTLY_EDITED_MODULE_ID);

    const topTermsModule = recommendationModules?.find((m) => m.moduleId === TOP_TERMS_MODULE_ID);
    const topTagsModule = recommendationModules?.find((m) => m.moduleId === TOP_TAGS_MODULE_ID);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const topGlossaryTermsData: GlossaryTermRow[] = (topTermsModule?.content ?? []).map((item: any, index: number) => ({
        id: index,
        urn: item.entity?.urn || '',
        term_name: item.entity?.properties?.name || item.entity?.name || 'Unknown',
        domain: item.entity?.domain?.domain?.properties?.name || '',
        governance_status: item.entity?.GovernanceStatusInfo?.status || GovernanceType.None,
        governance_display_name: item.entity?.GovernanceStatusInfo?.displayName || '',
        exists: item.entity?.exists,
    }));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mapAsset = (item: any, index: number, includeTime = true) => {
        const row = {
            id: index,
            urn: item.entity?.urn || '',
            asset_name: item.entity ? entityRegistry.getDisplayName(item.entity.type, item.entity) : 'Unknown',
            asset_type: item.entity?.type,
            domain: item.entity?.domain?.domain?.properties?.name,
            exists: item.entity?.exists,
        };
        return includeTime ? { ...row, timestamp: item.entity?.lastUpdated || new Date().toISOString() } : row;
    };

    const mostAccessedAssetsData = (mostAccessedModule?.content ?? []).map((item, index) =>
        mapAsset(item, index, false),
    ) as AssetRow[];
    const recentlyViewedAssetsData = (recentlyViewedModule?.content ?? []).map((item, index) =>
        mapAsset(item, index),
    ) as RecentlyAccessedAssetRow[];
    const recentlyEditedAssetsData = (recentlyEditedModule?.content ?? []).map((item, index) =>
        mapAsset(item, index),
    ) as RecentlyAccessedAssetRow[];

    return (
        <>
            <Col lg={12} xs={24} data-export-section>
                <PrivilegeGate isRestricted={(p) => !p?.manageDomains && !p?.viewDomain} title={t('Domains')}>
                    <ChartWidgetCard
                        title={t('Domains')}
                        loading={domainsLoading}
                        isEmpty={domainsChartCategories.length === 0}
                    >
                        <ColumnChart
                            series={domainsChartSeries}
                            categories={domainsChartCategories}
                            height={300}
                            showLegend
                        />
                    </ChartWidgetCard>
                </PrivilegeGate>
            </Col>
            <Col lg={12} xs={24} data-export-section>
                <Card
                    className="f-bordered f-dashboard-grid-card"
                    title={<WidgetCardHeader title={t('Recently Viewed Assets')} loading={recommendationsLoading} />}
                >
                    <StyledTable
                        rowKey="id"
                        size="small"
                        dataSource={recentlyViewedAssetsData}
                        columns={getAssetsColumns(entityRegistry)}
                        locale={getNoDataLocale(t('NO_DATA_AVAILABLE'))}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: false,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        }}
                    />
                </Card>
            </Col>
            <Col lg={12} xs={24} data-export-section>
                <PrivilegeGate
                    isRestricted={(p) => !p?.viewGlossaryTerm && !p?.manageGlossaryTerms}
                    title={t('Top Glossary Terms')}
                >
                    <Card
                        className="f-bordered f-dashboard-grid-card"
                        title={<WidgetCardHeader title={t('Top Glossary Terms')} loading={recommendationsLoading} />}
                    >
                        <StyledTable
                            rowKey="id"
                            size="small"
                            dataSource={topGlossaryTermsData}
                            columns={getGlossaryTermColumns(entityRegistry)}
                            locale={getNoDataLocale(t('NO_DATA_AVAILABLE'))}
                            pagination={{
                                pageSize: 5,
                                showSizeChanger: false,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                            }}
                        />
                    </Card>
                </PrivilegeGate>
            </Col>
            <Col lg={12} xs={24} data-export-section>
                <Card
                    className="f-bordered f-dashboard-grid-card"
                    title={<WidgetCardHeader title={t('Most Popular Assets')} loading={recommendationsLoading} />}
                >
                    <StyledTable
                        rowKey="id"
                        size="small"
                        dataSource={mostAccessedAssetsData}
                        columns={getMostAccessedAssetsGrid(entityRegistry)}
                        locale={getNoDataLocale(t('NO_DATA_AVAILABLE'))}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: false,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        }}
                    />
                </Card>
            </Col>

            <Col lg={12} xs={24} data-export-section>
                <Card
                    className="f-bordered f-dashboard-grid-card"
                    title={<WidgetCardHeader title={t('Most Edited Assets')} loading={recommendationsLoading} />}
                >
                    <StyledTable
                        rowKey="id"
                        size="small"
                        dataSource={recentlyEditedAssetsData}
                        columns={getAssetsColumns(entityRegistry)}
                        locale={getNoDataLocale(t('NO_DATA_AVAILABLE'))}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: false,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        }}
                    />
                </Card>
            </Col>
            <Col xs={24} data-export-section>
                <PrivilegeGate isRestricted={(p) => !p?.manageTags && !p?.viewTag} title={t('Top Tags')}>
                    <Card
                        className="f-bordered f-dashboard-grid-card"
                        style={{ height: 'fit-content' }}
                        title={<WidgetCardHeader title={t('Top Tags')} loading={recommendationsLoading} />}
                    >
                        {topTagsModule && (
                            <RecommendationModule
                                module={topTagsModule as RecommendationModuleType}
                                scenarioType={ScenarioType.Home}
                                showTitle={false}
                            />
                        )}
                    </Card>
                </PrivilegeGate>
            </Col>
        </>
    );
};
